import { IApiServiceConfig } from './../api/IApiServiceConfig';
import { API_SERVICE_CONFIG } from './../api/ApiServiceConfig';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  EndpointErrorMessages,
  GenericErrorMessages,
} from './ErrorMessages/ErrorHandlingMessages';
import { I18NextPipe } from 'angular-i18next';
import { companyDynamicErrorMessage } from './ErrorMessages/CompanyRequestErrorMessagens';
import { AuthEndpoint } from '../api/v0/AuthEndpoint';
import { AutenticanteErrorMessages } from './ErrorMessages/AutenticanteErrorMessages';
import { ToastService } from '@fullyops/shared/services/toast/toast.service';
import { versionMismatchStatus } from '@fullyops/core/interceptors/version-check.interceptor';
import { ERROR_WORK_ORDER_BLOCKED_WITHOUT_MOTIVE } from '@fullyops/data/work-orders/api-adapter.service';
import { AUTOMATIC_TOAST_ON_ERROR } from '@fullyops/core/errors/toast-on-error';

/**
 * Error codes whose handling is something other than displaying a message, and which
 * the component making the request therefore reacts to itself.
 *
 * A code that only has to be reported to the user does not belong here: the handling
 * below already displays it, by translating the code as a key.
 */
const silencedErrorCodes = {
  // Answered by prompting for a blocking motive and saving again.
  [ERROR_WORK_ORDER_BLOCKED_WITHOUT_MOTIVE]: true,
};

@Injectable()
export class ErrorHandingInterceptor implements HttpInterceptor {
  constructor(
    @Inject(API_SERVICE_CONFIG) private apiServiceConfig: IApiServiceConfig,
    private i18nextPipe: I18NextPipe,
    private toastService: ToastService,
    private auth: AuthEndpoint,
  ) {}

  getErrorMessage = (
    endPoint,
    status: HttpStatusCode,
    errorMessage?: string | { errors: Array<string> },
  ): string => {
    const getGenericErrorMessage = (statusCode: HttpStatusCode) =>
      GenericErrorMessages[statusCode] ||
      GenericErrorMessages[`${statusCode.toString()[0]}xx`] ||
      GenericErrorMessages['xxx'];
    const isErrorMessageObject = typeof errorMessage == 'object';

    if (status >= 500 && status <= 599) return getGenericErrorMessage(status);

    const companyDynamicErrorMessageResponse = companyDynamicErrorMessage({
      endPoint,
      errorMessage: isErrorMessageObject
        ? errorMessage.errors[0]
        : errorMessage,
    });

    if (companyDynamicErrorMessageResponse) {
      return companyDynamicErrorMessageResponse;
    }

    if (isErrorMessageObject && errorMessage?.errors) {
      if (errorMessage?.errors[0]) {
        return errorMessage?.errors[0];
      }
      if (!EndpointErrorMessages[endPoint]) {
        return getGenericErrorMessage(status);
      }

      if (!EndpointErrorMessages[endPoint][status]) {
        return getGenericErrorMessage(status);
      }
    } else if (typeof errorMessage === 'string') {
      if (!EndpointErrorMessages[endPoint]) {
        return getGenericErrorMessage(status);
      }
    }
    return EndpointErrorMessages?.[endPoint]?.[status]?.['default'] || 'error';
  };

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!req.context.get(AUTOMATIC_TOAST_ON_ERROR)) {
      return next.handle(req);
    }
    return next.handle(req).pipe(
      tap({
        error: (error: HttpErrorResponse) => {
          if (isSilencedError(error)) {
            return;
          }
          const hasNumber = /\d/;
          const endPointWithParams = req.url.split('api')[1];
          const endpointParts = endPointWithParams.split('/');
          const endpointValidParts = endpointParts.filter(
            (urlPart) => urlPart && !hasNumber.test(urlPart),
          );
          const endPoint = '/' + endpointValidParts.join('/');
          const errorMessage = this.getErrorMessage(
            endPoint,
            error.status,
            error.error,
          );
          // Values the backend sends for interpolating into the code's message, e.g. a field name.
          const params = error.error?.params ?? undefined;
          //  Open SnackBar
          this.toastService.showErrorMessage(
            this.i18nextPipe.transform(errorMessage, params),
          );
        },
      }),
    );
  }
}

function isSilencedError(err: HttpErrorResponse) {
  return (
    err.status === versionMismatchStatus ||
    (err.status === 409 && err.error?.error === 'QUOTA_EXCEEDED') ||
    (err.status === 400 &&
      err.error.errors instanceof Array &&
      err.error.errors.length === 1 &&
      silencedErrorCodes[err.error.errors[0]] === true)
  );
}
